<script lang="ts">
import { defineComponent, computed } from 'vue'
import ToastItem from '@/components/toast/ToastItem.vue'
import { useHeaderStore } from '@/stores/header'
import { useToastStore } from '@/stores/toast'
import { useMediaQueryStore } from '@/stores/mediaQuery'

export default defineComponent({
  name: 'ToastMessage',

  components: {
    ToastItem,
  },

  setup() {
    // store
    const headerStore = useHeaderStore()
    const toastStore = useToastStore()
    const mediaQueryStore = useMediaQueryStore()

    /**
     * トーストの表示位置
     *
     * @description SP 表示時 = 画面上から 16px 固定
     *              PC 表示時 = ヘッダーが表示されているとき（デフォルト）: 画面上から 96px
     *                         ヘッダーが表示されていないとき（画面スクロール時）: 画面上から 16px
     */
    const toastPosition = computed(() => {
      return !mediaQueryStore.context.isUnderSp && headerStore.context.isShow ? '96px' : '16px'
    })

    const leave = (el: Element) => {
      const toastId = el.getAttribute('data-toast-id') || undefined
      if (toastId) toastStore.removeToastItem(toastId)
    }

    const close = (toastId: string | undefined) => {
      if (toastId) toastStore.removeToastItem(toastId)
    }

    return {
      // store
      toastStore,
      headerStore,

      // computed
      toastPosition,

      // methods
      leave,
      close,
    }
  }
})
</script>

<template>
  <transition-group
    v-show="toastStore.context.items.length && toastStore.isInitialized"
    class="l-rows__toastMessage"
    tag="div"
    name="is-toast"
    :style="{ top: toastPosition }"
    @after-enter="leave($event)">
    <div
      v-for="item in toastStore.context.items"
      :key="item.id"
      :data-toast-id="item.id"
      class="l-rows__toastMessage--row"
      :style="{
        animationDuration: `${item.duration}s`,
      }">
      <ToastItem
        :message-type="item.messageType"
        @close="close(item.id)">
        {{ item.message }}
      </ToastItem>
    <!-- /.l-rows__toastMessage row --></div>
  <!-- /.l-rows__toastMessage --></transition-group>
</template>

<style lang="sass" scoped>
$toast-unit-height-em: math.div($toast-item-height, $font-normal-px)
$toast-item-margin-em: 0.5
$toast-item-computed-height: #{$toast-unit-height-em + $toast-item-margin-em}em

// layout
.l-rows__toastMessage
  $root: &
  width: 100%
  max-width: #{$global-contents-width-pc}px
  display: flex
  flex-direction: column-reverse
  position: fixed
  top: 96px
  left: 50%
  transform: translateX(-50%)
  z-index: 21
  +transition($duration: $transition-long)

  & &--row
    height: 0
    display: flex
    flex-direction: column
    overflow: hidden
    opacity: 0
    visibility: hidden

    &::after
      content: ""
      width: 100%
      height: #{$toast-item-margin-em}em
      display: block

  +breakpoint
    top: 16px
    padding: 0 12px

// vue transition
.is-toast
  &-enter-active
    animation:
      name: toastMessage
      fill-mode: forwards

@keyframes toastMessage
  0%
    height: 0
    opacity: 0
    visibility: hidden

  7%
    height: $toast-item-computed-height
    opacity: 1
    visibility: visible

  93%
    height: $toast-item-computed-height
    opacity: 1
    visibility: visible

  100%
    height: 0
    opacity: 0
    visibility: hidden
</style>
